import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import Confetti from 'react-confetti';
import HandwritingCanvas from '@/components/HandwritingCanvas';
import {
  ArrowLeft, Pencil, Lightbulb, Send, Star, Trophy, Sparkles,
  BookOpen, Clock, History, Loader2, Volume2, RefreshCw, Edit3
} from 'lucide-react';
import { speak as _speakTTS, stopSpeaking as _stopTTS } from '@/utils/speak';

const DailyWritingPrompt = () => {
  const { childId } = useParams();
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [prompt, setPrompt] = useState(null);
  const [child, setChild] = useState(null);
  const [response, setResponse] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState('');
  const [loadingSuggestion, setLoadingSuggestion] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [inputMode, setInputMode] = useState('text'); // 'text' or 'canvas'
  const [canvasData, setCanvasData] = useState(null);

  useEffect(() => {
    fetchPromptAndChild();
    fetchHistory();
  }, [childId]);

  const fetchPromptAndChild = async () => {
    try {
      const [promptRes, childRes] = await Promise.all([
        api.get(`/writing/daily-prompt/${childId}`),
        api.get(`/children/${childId}`)
      ]);
      setPrompt(promptRes.data);
      setChild(childRes.data);
      
      if (promptRes.data.completed && promptRes.data.submission) {
        setResponse(promptRes.data.submission);
        setSubmitted(true);
      }
    } catch (error) {
      console.error('Failed to fetch prompt:', error);
      toast.error('Failed to load today\'s writing prompt');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await api.get(`/writing/history/${childId}`);
      setHistory(res.data.history);
    } catch (error) {
      console.error('Failed to fetch history:', error);
    }
  };

  const speakText = (text) => {
    _speakTTS(text, 0.9);
  };

  const getAiSuggestion = async () => {
    if (!prompt) return;
    setLoadingSuggestion(true);
    try {
      const res = await api.get(`/writing/daily-prompt/${prompt.id}/ai-suggestion`);
      setAiSuggestion(res.data.suggestion);
      speakText(res.data.suggestion);
    } catch (error) {
      toast.error('Failed to get suggestion');
    } finally {
      setLoadingSuggestion(false);
    }
  };

  const handleSubmit = async () => {
    if (!response.trim() && !canvasData) {
      toast.error('Please write something before submitting!');
      return;
    }
    
    const submissionText = inputMode === 'text' ? response : `[Handwritten response - ${canvasData ? 'Canvas data submitted' : 'No data'}]`;
    
    setSubmitting(true);
    try {
      const res = await api.post(`/writing/daily-prompt/${prompt.id}/submit?submission=${encodeURIComponent(submissionText)}`);
      setResult(res.data);
      setSubmitted(true);
      setShowConfetti(true);
      toast.success(`Great job! You earned ${res.data.xp_earned} XP!`);
      setTimeout(() => setShowConfetti(false), 5000);
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const getCategoryEmoji = (category) => {
    const emojis = {
      narrative: '📖',
      descriptive: '🎨',
      persuasive: '💬',
      imaginative: '✨',
      reflective: '💭'
    };
    return emojis[category] || '📝';
  };

  const getCategoryColor = (category) => {
    const colors = {
      narrative: 'bg-blue-100 text-blue-700',
      descriptive: 'bg-purple-100 text-purple-700',
      persuasive: 'bg-amber-100 text-amber-700',
      imaginative: 'bg-pink-100 text-pink-700',
      reflective: 'bg-teal-100 text-teal-700'
    };
    return colors[category] || 'bg-slate-100 text-slate-700';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-cyan-50 to-white">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-cyan-50 to-white">
      {showConfetti && <Confetti recycle={false} numberOfPieces={200} />}
      
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-cyan-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to={`/learn/${childId}`}>
                <Button variant="ghost" size="icon">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center">
                  <Pencil className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="font-fredoka text-xl font-bold text-cyan-800">Daily Writing</h1>
                  <p className="text-sm text-slate-500">{child?.name}'s Writing Corner</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowHistory(!showHistory)}
                className="gap-1"
                data-testid="history-btn"
              >
                <History className="w-4 h-4" />
                History
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <AnimatePresence mode="wait">
          {showHistory ? (
            <motion.div
              key="history"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <History className="w-5 h-5 text-cyan-500" />
                      Your Writing History
                    </CardTitle>
                    <Button variant="ghost" size="sm" onClick={() => setShowHistory(false)}>
                      Back to Today's Prompt
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {history.length === 0 ? (
                    <p className="text-center text-slate-500 py-8">
                      No completed writing prompts yet. Start with today's prompt!
                    </p>
                  ) : (
                    <div className="space-y-4">
                      {history.map((item) => (
                        <div key={item.id} className="border rounded-lg p-4">
                          <div className="flex items-start justify-between mb-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(item.category)}`}>
                              {getCategoryEmoji(item.category)} {item.category}
                            </span>
                            <span className="text-xs text-slate-500">{item.date}</span>
                          </div>
                          <p className="font-medium text-slate-700 mb-2">{item.prompt}</p>
                          <p className="text-sm text-slate-600 line-clamp-3">{item.submission}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          ) : (
            <motion.div
              key="prompt"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              {/* Today's Prompt Card */}
              <Card className="border-2 border-cyan-200 bg-gradient-to-br from-white to-cyan-50">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getCategoryColor(prompt?.category)}`}>
                        {getCategoryEmoji(prompt?.category)} {prompt?.category}
                      </span>
                      <span className="text-sm text-slate-500">Today's Prompt</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => speakText(prompt?.prompt)}
                      data-testid="speak-prompt-btn"
                    >
                      <Volume2 className="w-5 h-5 text-cyan-500" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-fredoka text-slate-800 leading-relaxed" data-testid="prompt-text">
                    {prompt?.prompt}
                  </p>
                </CardContent>
              </Card>

              {/* AI Suggestion */}
              {!submitted && (
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Lightbulb className="w-5 h-5 text-amber-500" />
                        <span className="font-medium text-slate-700">Need help getting started?</span>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={getAiSuggestion}
                        disabled={loadingSuggestion}
                        data-testid="get-suggestion-btn"
                      >
                        {loadingSuggestion ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Sparkles className="w-4 h-4 mr-1" />
                            Get a Tip
                          </>
                        )}
                      </Button>
                    </div>
                    {aiSuggestion && (
                      <motion.p
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mt-3 p-3 bg-amber-50 rounded-lg text-amber-800 text-sm"
                      >
                        💡 {aiSuggestion}
                      </motion.p>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Writing Area */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <Edit3 className="w-5 h-5 text-cyan-500" />
                      Your Response
                    </CardTitle>
                    {!submitted && (
                      <div className="flex gap-2">
                        <Button
                          variant={inputMode === 'text' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setInputMode('text')}
                          data-testid="text-mode-btn"
                        >
                          Type
                        </Button>
                        <Button
                          variant={inputMode === 'canvas' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setInputMode('canvas')}
                          data-testid="canvas-mode-btn"
                        >
                          Write/Draw
                        </Button>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {submitted ? (
                    <div className="space-y-4">
                      <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                        <div className="flex items-center gap-2 mb-3">
                          <Trophy className="w-5 h-5 text-green-600" />
                          <span className="font-bold text-green-700">Great job completing today's prompt!</span>
                        </div>
                        {result && (
                          <div className="flex gap-4">
                            <div className="flex items-center gap-1">
                              <Star className="w-4 h-4 text-amber-500" />
                              <span className="text-sm font-medium">{result.xp_earned} XP</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="text-sm text-slate-600">{result.word_count} words</span>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="bg-slate-50 rounded-lg p-4">
                        <p className="text-slate-700 whitespace-pre-wrap">{response}</p>
                      </div>
                    </div>
                  ) : inputMode === 'text' ? (
                    <Textarea
                      value={response}
                      onChange={(e) => setResponse(e.target.value)}
                      placeholder="Start writing your response here... Let your imagination flow!"
                      className="min-h-[200px] text-lg"
                      data-testid="writing-textarea"
                    />
                  ) : (
                    <div className="border-2 border-dashed border-cyan-200 rounded-lg p-4">
                      <HandwritingCanvas
                        onSave={(data) => setCanvasData(data)}
                        prompt="Write or draw your response"
                        height={300}
                      />
                    </div>
                  )}
                  
                  {!submitted && (
                    <div className="mt-4 flex items-center justify-between">
                      <div className="text-sm text-slate-500">
                        {inputMode === 'text' && (
                          <span>{response.split(/\s+/).filter(w => w).length} words</span>
                        )}
                      </div>
                      <Button
                        onClick={handleSubmit}
                        disabled={submitting}
                        className="bg-cyan-500 hover:bg-cyan-600"
                        data-testid="submit-writing-btn"
                      >
                        {submitting ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Send className="w-4 h-4 mr-2" />
                        )}
                        Submit Writing
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Word Count Progress */}
              {!submitted && inputMode === 'text' && (
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-600">Writing Progress</span>
                      <span className="text-sm text-slate-500">Goal: 50+ words</span>
                    </div>
                    <Progress 
                      value={Math.min((response.split(/\s+/).filter(w => w).length / 50) * 100, 100)} 
                      className="h-3"
                    />
                    <div className="flex justify-between mt-2 text-xs text-slate-500">
                      <span>25 XP base</span>
                      <span>+10 XP at 50 words</span>
                      <span>+15 XP at 100 words</span>
                    </div>
                  </CardContent>
                </Card>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

export default DailyWritingPrompt;
